import React from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../../lib/format";
import { fmtDurationSeconds } from "../../lib/dashboardDates";

// ---------------------------------------------------------------------------
// KPI card & skeleton (stateless, pure presentational).
// ---------------------------------------------------------------------------
function KpiCard({ label, value, sub, accent = "muted", clickable = true, onClick, testid, hint }) {
  const className = `qw-kpi accent-${accent}${clickable ? "" : " static"}`;
  const content = (
    <>
      <div className="k-label">{label}</div>
      <div className="k-value">{value}</div>
      {sub ? <div className="k-sub">{sub}</div> : null}
      {clickable && hint ? <div className="k-hint">{hint}</div> : null}
    </>
  );
  if (!clickable || !onClick) {
    return (
      <div className={className} data-testid={testid} aria-label={typeof label === "string" ? label : undefined}>
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={className}
      data-testid={testid}
      aria-label={typeof label === "string" ? label : undefined}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

function KpiSkeleton() {
  return (
    <div className="qw-kpi static" aria-busy="true">
      <div className="qw-skel h-16 w-60" />
      <div style={{ height: 8 }} />
      <div className="qw-skel h-24 w-80" />
      <div style={{ height: 8 }} />
      <div className="qw-skel h-16 w-40" />
    </div>
  );
}

// Stable module-level configuration. Keys are semantic (never index-based).
const PERIOD_SKELETON_KEYS = [
  "sent",
  "opened",
  "responded",
  "accepted",
  "declined",
  "accepted-value",
  "avg-open",
  "avg-response",
];
const CURRENT_SKELETON_KEYS = [
  "awaiting",
  "expiring-7",
  "expired",
  "unassigned",
  "active-value",
];

export function PeriodKpiGrid({ pk, loading, onMetricClick }) {
  const { t } = useTranslation();
  if (loading || !pk) {
    return (
      <div className="qw-grid kpis">
        {PERIOD_SKELETON_KEYS.map((k) => (
          <KpiSkeleton key={`skel-${k}`} />
        ))}
      </div>
    );
  }
  return (
    <div className="qw-grid kpis">
      <KpiCard
        testid="kpi-sent-count"
        accent="info"
        label={t("dashboard.kpi_sent_count")}
        value={pk.sent_count.toLocaleString("bg-BG")}
        sub={`${t("dashboard.kpi_conversion_pct")}: ${pk.conversion_pct.toFixed(1)}%`}
        onClick={() => onMetricClick("sent_cohort")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-opened-count"
        accent="info"
        label={t("dashboard.kpi_opened_count")}
        value={pk.opened_count.toLocaleString("bg-BG")}
        sub={`${pk.open_rate_pct.toFixed(1)}% ${t("dashboard.sub_of_sent")}`}
        onClick={() => onMetricClick("opened_cohort")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-responded-count"
        accent="warning"
        label={t("dashboard.kpi_responded_count")}
        value={pk.responded_count.toLocaleString("bg-BG")}
        sub={`${pk.response_rate_pct.toFixed(1)}% ${t("dashboard.sub_of_sent")}`}
        onClick={() => onMetricClick("responded_cohort")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-accepted-from-cohort"
        accent="success"
        label={t("dashboard.kpi_accepted_from_cohort")}
        value={pk.accepted_from_cohort.toLocaleString("bg-BG")}
        sub={t("dashboard.sub_pct_conversion")}
        onClick={() => onMetricClick("accepted_cohort")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-declined-from-cohort"
        accent="danger"
        label={t("dashboard.kpi_declined_from_cohort")}
        value={pk.declined_from_cohort.toLocaleString("bg-BG")}
        onClick={() => onMetricClick("declined_cohort")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-accepted-value-period"
        accent="success"
        label={t("dashboard.kpi_accepted_value_period")}
        value={<span className="money">{fmtMoney(pk.accepted_value_period, "EUR")}</span>}
        onClick={() => onMetricClick("accepted_value_period")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-avg-time-to-open"
        accent="muted"
        clickable={false}
        label={t("dashboard.kpi_avg_time_to_open")}
        value={fmtDurationSeconds(pk.avg_seconds_to_open, t)}
      />
      <KpiCard
        testid="kpi-avg-time-to-response"
        accent="muted"
        clickable={false}
        label={t("dashboard.kpi_avg_time_to_response")}
        value={fmtDurationSeconds(pk.avg_seconds_to_response, t)}
      />
    </div>
  );
}

export function CurrentKpiGrid({ ck, loading, onMetricClick, ownerFilterActive = false }) {
  const { t } = useTranslation();
  if (loading || !ck) {
    return (
      <div className="qw-grid kpis">
        {CURRENT_SKELETON_KEYS.map((k) => (
          <KpiSkeleton key={`skel-${k}`} />
        ))}
      </div>
    );
  }
  const unassignedDisabled = ownerFilterActive;
  return (
    <div className="qw-grid kpis">
      <KpiCard
        testid="kpi-awaiting-response"
        accent="warning"
        label={t("dashboard.kpi_awaiting_response")}
        value={ck.awaiting_response_count.toLocaleString("bg-BG")}
        sub={fmtMoney(ck.awaiting_response_value, "EUR")}
        onClick={() => onMetricClick("awaiting_response")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-expiring-7"
        accent="warning"
        label={t("dashboard.kpi_expiring_7")}
        value={ck.expiring_7_count.toLocaleString("bg-BG")}
        sub={fmtMoney(ck.expiring_7_value, "EUR")}
        onClick={() => onMetricClick("expiring_7_days")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-expired-current"
        accent="danger"
        label={t("dashboard.kpi_expired_current")}
        value={ck.expired_current_count.toLocaleString("bg-BG")}
        sub={fmtMoney(ck.expired_current_value, "EUR")}
        onClick={() => onMetricClick("expired_current")}
        hint={t("dashboard.click_hint")}
      />
      <KpiCard
        testid="kpi-unassigned-current"
        accent="unassigned"
        label={t("dashboard.kpi_unassigned_current")}
        value={(ck.unassigned_current_count || 0).toLocaleString("bg-BG")}
        sub={fmtMoney(ck.unassigned_current_value || 0, "EUR")}
        clickable={!unassignedDisabled}
        onClick={() => onMetricClick("unassigned_current")}
        hint={
          unassignedDisabled
            ? t("dashboard.unassigned_disabled_when_owner_filtered")
            : t("dashboard.click_hint")
        }
      />
      <KpiCard
        testid="kpi-active-value"
        accent="primary"
        label={t("dashboard.kpi_active_value")}
        value={<span className="money">{fmtMoney(ck.active_value, "EUR")}</span>}
        onClick={() => onMetricClick("active_value")}
        hint={t("dashboard.click_hint")}
      />
    </div>
  );
}
